// Security.tsx — the three things WIRING_REQUIRED.md §6 says are derivable now.
//
//   2FA coverage      — users.totp_enabled, via GET /tenants/:id/users per customer
//   SSO coverage      — the recorded sign-in method, via GET /tenants/:id/onboarding
//   Sensitive actions — GET /audit, filtered to the security-relevant action set
//
// The 2FA and SSO figures need one request per customer, because no aggregate
// endpoint exists. That is honest N+1 on an operator screen with a handful of
// customers, and it is bounded and stated on screen — better than inventing an
// endpoint or leaving the panel empty. Requests run concurrently and partial
// failures are reported per customer rather than collapsing the whole number.
//
// Failed-login trend, stale sessions and the compliance checklist are §6's
// "needs building" half and are marked as such, not rendered as zeros.
//
// AUDIT NOISE IS DELIBERATE, NOT A BUG. Loading this screen writes one
// `operator_users_viewed` row per customer, which is visible in this screen's own
// sensitive-actions feed. That was checked against the existing convention before
// being changed, and the convention is to audit cross-tenant READS:
// operator_tenant_health_viewed, operator_users_viewed, operator_ai_key_viewed and
// operator_sso_viewed all exist in platformAdmin.js. Reading another tenant's user
// list IS the sensitive act, so suppressing the record to tidy the feed would
// remove a control to fix a cosmetic complaint. The screen discloses the cost
// instead.

import React, { useCallback, useEffect, useState } from "react";
import { PT, fmtNum } from "../../platformTheme";
import { pget, perr } from "./api";
import { PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow, PStat, PGrid, PId, th, td, tdNum, fmtDate } from "./ui";
import { BarList, NotCaptured } from "./charts";
import type { Tenant } from "./Customers";

// Actions worth surfacing on a security screen: anything that reveals, stages or
// re-keys a secret, or changes who holds cross-tenant power.
const SENSITIVE = /secret|credential|cred\.|ai_key|api_key|sso|operator|permission|role|delete|purge|reveal|impersonat/i;

interface TenantSecurity {
  tenant: Tenant;
  users: number | null;
  with2fa: number | null;
  signin: string | null;
  error?: string;
}

export const Security: React.FC<{ onOpenCustomer: (t: Tenant) => void }> = ({ onOpenCustomer }) => {
  const [rows, setRows] = useState<TenantSecurity[] | null>(null);
  const [audit, setAudit] = useState<any[] | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    setRows(null); setAudit(null);

    const tl = await pget("/tenants");
    if (!tl.ok) { setErr(perr(tl, "Could not load customers")); setRows([]); setAudit([]); return; }
    const tenants: Tenant[] = tl.data?.tenants || [];

    const results = await Promise.all(tenants.map(async (t): Promise<TenantSecurity> => {
      const [u, o] = await Promise.all([
        pget(`/tenants/${t.tenant_id}/users`),
        pget(`/tenants/${t.tenant_id}/onboarding`),
      ]);
      if (!u.ok) return { tenant: t, users: null, with2fa: null, signin: null, error: perr(u, "users unavailable") };
      const list: any[] = u.data?.users || [];
      const prof = o.ok ? (o.data?.onboarding || o.data?.profile || o.data || {}) : {};
      const signin = prof.signin_method || prof.sign_in_method || prof.signinMethod || null;
      return {
        tenant: t,
        users: list.length,
        with2fa: list.filter((x) => x.totp_enabled).length,
        signin: signin ? String(signin) : null,
      };
    }));
    setRows(results);

    const a = await pget("/audit", { limit: 400 });
    setAudit(a.ok ? (a.data?.entries || []).filter((e: any) => SENSITIVE.test(String(e.action || ""))) : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const ok = (rows || []).filter((r) => !r.error);
  const totalUsers = ok.reduce((a, r) => a + (r.users || 0), 0);
  const total2fa = ok.reduce((a, r) => a + (r.with2fa || 0), 0);
  const pct2fa = totalUsers ? (total2fa / totalUsers) * 100 : null;
  const ssoTenants = ok.filter((r) => (r.signin || "").toLowerCase().includes("sso")).length;
  const knownSignin = ok.filter((r) => r.signin).length;
  const failedProbes = (rows || []).filter((r) => r.error).length;

  return (
    <>
      <PBanner>{err}</PBanner>

      <PSection
        title="Security posture"
        sub="derived live from customer users and recorded onboarding profiles — each load records one cross-tenant read per customer"
        right={<PBtn small onClick={load}>Refresh</PBtn>}
      >
        <PGrid min={185}>
          <PStat
            label="2FA coverage"
            value={rows === null ? "…" : pct2fa == null ? "—" : `${pct2fa.toFixed(0)}%`}
            sub={rows === null ? "" : `${fmtNum(total2fa)} of ${fmtNum(totalUsers)} users`}
          />
          <PStat
            label="SSO coverage"
            value={rows === null ? "…" : `${ssoTenants}/${ok.length}`}
            sub={knownSignin < ok.length ? `${ok.length - knownSignin} without a recorded method` : "customers on SSO"}
          />
          <PStat
            label="Sensitive actions"
            value={audit === null ? "…" : fmtNum(audit.length)}
            sub="in the fetched audit page"
          />
          <PStat
            label="Probe failures"
            value={rows === null ? "…" : fmtNum(failedProbes)}
            sub={failedProbes ? "customers whose users could not be read" : "all customers readable"}
          />
        </PGrid>
      </PSection>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14, marginBottom: 22 }}>
        <PCard>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 11 }}>2FA coverage by customer</div>
          {rows === null ? <PSpinner /> : ok.length === 0 ? <PEmpty>No customers.</PEmpty> : (
            <BarList
              rows={ok.map((r) => ({
                label: r.tenant.name,
                value: r.users ? Math.round(((r.with2fa || 0) / r.users) * 100) : 0,
                sub: `${r.with2fa}/${r.users}`,
                color: r.users && (r.with2fa || 0) / r.users >= 0.8 ? PT.ok : PT.warn,
                onClick: () => onOpenCustomer(r.tenant),
              }))}
              format={(v) => `${v}%`}
            />
          )}
        </PCard>

        <PCard style={{ padding: 0 }}>
          <div style={{ padding: "13px 16px", borderBottom: `1px solid ${PT.brdSubtle}`, fontSize: 13.5, fontWeight: 600 }}>
            Sign-in method by customer
          </div>
          {rows === null ? <PSpinner /> : (
            <PTable minWidth={380} head={<><th style={th}>Customer</th><th style={th}>Method</th><th style={{ ...th, textAlign: "right" }}>Users</th></>}>
              {(rows || []).map((r) => (
                <PRow key={r.tenant.tenant_id} onClick={() => onOpenCustomer(r.tenant)}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.tenant.name}</div>
                    <PId id={r.tenant.tenant_id} />
                  </td>
                  <td style={td}>
                    {r.error
                      ? <PBadge tone="err">unreadable</PBadge>
                      : r.signin
                        ? <PBadge tone={r.signin.toLowerCase().includes("sso") ? "ok" : "neutral"}>{r.signin}</PBadge>
                        : <span style={{ color: PT.txD, fontSize: 11.5 }}>not recorded</span>}
                  </td>
                  <td style={tdNum}>{r.users == null ? "—" : fmtNum(r.users)}</td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </div>

      <PSection title="Recent sensitive actions" sub="operator_audit, filtered to secret, credential, SSO, operator and permission changes">
        <PCard style={{ padding: 0 }}>
          {audit === null ? <PSpinner /> : audit.length === 0 ? (
            <PEmpty>No sensitive operator actions in the fetched page.</PEmpty>
          ) : (
            <PTable
              minWidth={720}
              head={<><th style={th}>Time</th><th style={th}>Operator</th><th style={th}>Customer</th><th style={th}>Action</th><th style={th}>Reason</th></>}
            >
              {audit.slice(0, 60).map((e) => (
                <PRow key={e.id}>
                  <td style={{ ...td, whiteSpace: "nowrap", color: PT.txM, fontSize: 11.5 }}>{fmtDate(e.occurred_at)}</td>
                  <td style={td}><PId id={e.actor_user_id} /></td>
                  <td style={td}><PId id={e.target_tenant_id} /></td>
                  <td style={td}><PBadge tone="warn">{e.action}</PBadge></td>
                  <td style={{ ...td, color: PT.txM }}>{e.reason || <span style={{ color: PT.txD }}>—</span>}</td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </PSection>

      <PSection title="Not captured yet" sub="WIRING_REQUIRED.md §6, the 'needs building' half">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
          <NotCaptured
            what="Failed-login trend"
            needs="Authentication failures are not persisted in a queryable form, so there is no 14-day series to plot. Needs an auth-failure log plus a daily rollup."
          />
          <NotCaptured
            what="Stale sessions"
            needs="Sessions are stateless JWTs — there is no session store to count idle sessions in. Needs either server-side session records or refresh-token tracking."
          />
          <NotCaptured
            what="Compliance checklist"
            needs="ZDR, data residency, encryption, audit retention and pen-test date are not modelled. Needs a small attestation store; some entries are static config, others derived."
          />
        </div>
      </PSection>
    </>
  );
};
